/**
 * Created by Alan Yang on 4/09/2019.
 * Description:
 * DataImportComponent
 *
 * ------ maintenance history ------
 * updated by Alan Yang on Mar.21 2019
 * Added Handle intertional dates logic. eg: columnTypeOptions related.

 * updated by Bowen Li on 05/24/2019
 * changed the float menu showing
 *
 * updated by Alan Yang on 7/3/2019
 * added logic 'If user replace flat file with same headers, column type and provider column setting should be kept.'
 *
 * Updated by Abner Sui on 07/10/2019
 * Added detect encoding
 *
 * updated by Alan Yang on 8/23 2019
 * Added SQL MDL support
 */
import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { catchError, take } from 'rxjs/operators';
import { of as observableOf } from 'rxjs';
import { Store, select } from '@ngrx/store';

import { AppConfig } from '../../tamalelibs/models/app-config.model';
import { Dataprovider, ProviderColumnType } from '../../tamalelibs/models/dataprovider.model';
import { DataProviderService } from '../../tamalelibs/services/dataprovider.service';
import { StoreQuerierService } from '../../services/store-querier.service';
import { FloatMenuService } from '../../services/float-menu.service';
import { TamUploadConfig, TamUploadUIFile } from '../../widgets/tam-upload/tam-upload.model';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { ToastService } from '../../widgets/toast/toast.service';
import { DataproviderActionTypes } from '../../redux/actions/dataprovider.actions';
import { AppState } from '../../redux';
import { SystemUser } from '../../tamalelibs/models/user.model';

import * as jschardet from 'jschardet';
import * as _lodash from 'lodash';
import * as Papaparse from 'papaparse';
import { AuthService } from '../../tamalelibs/services/auth.service';
import { GlobalActionType, GlobalService } from '../../tamalelibs/services/global.service';
import { AppLoadService } from '../../app-load.service';

@Component({
    selector: 'tam-data-import',
    templateUrl: './data-import.component.html',
    styleUrls: ['./data-import.component.scss']
})
export class DataImportComponent implements OnInit, OnDestroy {
    SQL_MDL_PASSWORD_KEY = '';
    uploadConfig: TamUploadConfig;
    uploadEditing = 0;
    btnDisabled = false; // if there is no file on dropzone, disable save button.
    pageTitle = '';
    currentUser: SystemUser;
    isFileValid = true;
    isFileChanged = false; // if the file changed in dropzone.
    sqlMdlPassword = '';

    // set 2 flags rather than a boolean one, it's for init case to load nothing instead one of them.
    isFlatfile = false; // flag for display tam-dialog
    isSqlmdl = false; // flag for display SQL MDL controls
    isOraclemdl = false;
    isConnectedSuccess = false; // flag for connect SQL MDL server
    isQuerySuccess = false; // flag for connect SQL query
    isConnectSuccessMessageShow = false; // flag for server has connect successfully to show conncet message
    isMDLConfigModified = { serverip: false, userName: false, password: false };

    // the 2 flags are for connect and run button loading spin
    isConnectting = false; // it stands that is connectting now to get database
    isRunning = false;
    isProgressing = false;
    // the Values to the data-import page UI elemments
    pagePropertiesObject = {
        description: '', entityReference: 'SHORTNAME', aliasPrefix: '',
        type: 1, name: '', filename: '', columns: [], providerColumn: '',
        previewRows: false
    }; // the edited row object, it's querybyid from server.

    // keep last pagePropertiesObject value on selected file.
    originalPagePropertiesObject = this.pagePropertiesObject;

    providerColumnList = []; // the data model of provider column.
    entityReferenceNameList = ['Shortname', 'Name', 'Alias'];

    // List of the Data Preview column types
    selectedPreviewTypeList = [];
    // keep last selectedPreviewTypeList value on selected file.
    originalSelectedPreviewTypeList = [];

    // change date to DMY/MDY/YMD
    columnTypeOptions = [
        { name: 'Text', value: 'character varying(255)' },
        { name: 'Number', value: 'numeric' },
        { name: 'Date (DD/MM/YYYY)', value: 'date', format: 'DMY' },
        { name: 'Date (MM/DD/YYYY)', value: 'date', format: 'MDY' },
        { name: 'Date (YYYY/MM/DD)', value: 'date', format: 'YMD' }
    ];

    // #region FormGroup Definition

    flatfileForm = new UntypedFormGroup({
        name: new UntypedFormControl(''),
        description: new UntypedFormControl(''),
        providerColumn: new UntypedFormControl(''),
        entityReferenceOptions: new UntypedFormControl(''),
        aliasPrefix: new UntypedFormControl(''),
        mdlProperties: new UntypedFormGroup({
            serverip: new UntypedFormControl(''),
            loginUser: new UntypedFormControl(''),
            loginPassword: new UntypedFormControl(''),
            database: new UntypedFormControl(''),
            mdlQuery: new UntypedFormControl(''),
        }),
    });

    get name() {
        return this.flatfileForm.get('name');
    }

    get description() {
        return this.flatfileForm.get('description');
    }

    get providerColumn() {
        return this.flatfileForm.get('providerColumn');
    }

    get entityReferenceOptions() {
        return this.flatfileForm.get('entityReferenceOptions');
    }

    get aliasPrefix() {
        return this.flatfileForm.get('aliasPrefix');
    }

    get serverip() {
        return this.flatfileForm.get('mdlProperties.serverip');
    }

    get loginUser() {
        return this.flatfileForm.get('mdlProperties.loginUser');
    }

    get loginPassword() {
        return this.flatfileForm.get('mdlProperties.loginPassword');
    }

    get database() {
        return this.flatfileForm.get('mdlProperties.database');
    }

    get mdlQuery() {
        return this.flatfileForm.get('mdlProperties.mdlQuery');
    }

    databaseList = [];
    // #endregion

    private _selectedRowId = '';
    private _operation = 'new';
    private _dataType = 'flatfile';
    private _dropedFile: any; // the accepted file in dropzone.
    private _navigateBySelf = false;

    constructor(
        private _appLoadService: AppLoadService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _storeQuerier: StoreQuerierService,
        private _toast: ToastService,
        private _floatMenuService: FloatMenuService,
        private _dataProviderService: DataProviderService,
        private _store: Store<AppState>,
        private _authService: AuthService,
        private _globalService: GlobalService,
    ) { }

    ngOnInit() {
        this._floatMenuService.floatMenuBottomSubject$.next(false);
        this._globalService.actionSubject$.next({
            type: GlobalActionType.DISABLE_GLOBAL_BUFFER,
            payload: true,
        });
        this.currentUser = this._storeQuerier.getCurrentUser();
        if (!this.currentUser.credential.publishPubConfiguration) {
            this._navigateBySelf = true;
            this._router.navigate(['settings', 'data']);
            this._toast.notify({
                message: 'you are not allowed to access this page',
                style: NotificationStyles.Error
            });
            return;
        }

        this.selectedPreviewTypeList = [];

        this._initUpload(''); // init dropzone.

        this._route.queryParams.subscribe(params => {
            this._selectedRowId = params.id;
            this._operation = params.action;
            this._dataType = params.type;

            // type value: 0 rich client, 1 flat file, 2 sql mdl
            if (this._dataType === '1') {
                this.isFlatfile = true;
                this.isSqlmdl = false;
                this.isOraclemdl = false;
                this.pageTitle = 'New Flat File Data Import';
                if (this._operation === 'edit') {
                    this._loadFlatfileData(this._selectedRowId);
                }
            } else if (this._dataType === '2') {
                this.isFlatfile = false;
                this.isSqlmdl = true;
                this.isOraclemdl = false;
                this.pageTitle = 'New SQL MDL Data Import';
                if (this._operation === 'edit') {
                    this._loadSqlmldData(this._selectedRowId);
                }
            } else if (this._dataType === '3') {
                this.isFlatfile = false;
                this.isSqlmdl = false;
                this.isOraclemdl = true;
                this.pageTitle = 'New Oracle MDL Data Import';
                if (this._operation === 'edit') {
                    this._loadODBCmdlData(this._selectedRowId);
                }
            }
        });

        this.SQL_MDL_PASSWORD_KEY = this._authService.mdlTokenDesKey();
    }

    ngOnDestroy(): void {
        this._floatMenuService.floatMenuBottomSubject$.next(true);
        this._globalService.actionSubject$.next({
            type: GlobalActionType.DISABLE_GLOBAL_BUFFER,
            payload: false,
        });
    }

    /**
     * Prompt dialog on refresh page.
     * @param {*} $event
     * @memberof DataImportComponent
     */
    @HostListener('window:beforeunload', ['$event'])
    unloadNotification($event: any) {
        if (this.flatfileForm.dirty || this.isFileChanged) {
            $event.returnValue = true;
        }
    }

    /**
     * Cancel button clicked
     *
     * Checks if the user has changed the value of this control in the UI.
     * the logic is in DataImportGuard.
     *
     * @memberof DataImportComponent
     */
    onBtnCancel() {
        this._navigateBySelf = false;
        this._router.navigate(['settings', 'data']);
    }

    onBtnSave() {
        // set value to provider.
        this.pagePropertiesObject.name = this.name.value;
        this.pagePropertiesObject.type = 1; // flat file by default. SQL MDL will cover it.
        this.pagePropertiesObject.description = this.description.value;
        this.pagePropertiesObject.providerColumn = this.providerColumn.value;
        this.pagePropertiesObject.aliasPrefix = this.aliasPrefix.value;
        this.pagePropertiesObject.entityReference = this.entityReferenceOptions.value.toUpperCase(); // server uses uppercase.

        if (this._doValidation()) {
            this.isProgressing = true;
            if (this.isFlatfile) {
                if (this.flatfileForm.invalid || !this.isFileValid) {
                    return;
                }
                if (this._operation === 'edit') {
                    this._dataProviderService.updateFlatfile(this._selectedRowId, this.pagePropertiesObject, this._dropedFile).pipe(
                        take(1),
                        catchError((e) => {
                            this._toast.notify({
                                message: e.message,
                                style: NotificationStyles.Error
                            });
                            this.isProgressing = false;
                            return observableOf(null);
                        }),
                    ).subscribe((res) => {
                        if (res) {
                            this.isProgressing = false;
                            this._flatFileSuccessHandle(res);
                        }
                    });
                } else {
                    this._dataProviderService.createFlatfile(this.pagePropertiesObject, this._dropedFile).pipe(
                        take(1),
                        catchError((e) => {
                            this._toast.notify({
                                message: e.message,
                                style: NotificationStyles.Error
                            });
                            this.isProgressing = false;
                            return observableOf(null);
                        }),
                    ).subscribe((res) => {
                        if (res) {
                            this.isProgressing = false;
                            this._flatFileSuccessHandle(res);
                        }
                    });
                }
            } else if (this.isSqlmdl || this.isOraclemdl) {
                // if user change/first input the password and has successed to connect, no need to change the sqlmdlpassword
                if (!this.isMDLConfigModified.password) {
                    // it means that: user not change the password to save the screen,
                    // we just need to save the server posted the value just memont
                    this.sqlMdlPassword = this.loginPassword.value;
                } else if (this.isMDLConfigModified.password && this.isConnectedSuccess) {
                    // it means that user change the password, but (1)not connect
                    // (2) failed connect
                    this.sqlMdlPassword = 'Basic ' + this._authService.encryptPasswordForWeb
                        (this.loginPassword.value, this.SQL_MDL_PASSWORD_KEY);
                }
                let tmpPagePropertiesObject = null;
                if (this.isSqlmdl) {
                    tmpPagePropertiesObject = {
                        description: '', entityReference: 'SHORTNAME', aliasPrefix: '',
                        type: 2, name: '', filename: '', columns: [], providerColumn: '',
                        previewRows: false,
                        servername: '',
                        port: '',
                        username: '',
                        password: '',
                        database: '',
                        query: '',
                    };
                } else {
                    tmpPagePropertiesObject = {
                        description: '', entityReference: 'SHORTNAME', aliasPrefix: '',
                        type: 3, name: '', filename: '', columns: [], providerColumn: '',
                        previewRows: false,
                        servername: '',
                        port: '',
                        servicename: '',
                        username: '',
                        password: '',
                        query: '',
                        schema: '',
                    };
                }

                // use pagePropertiesObject and add more properties for SQL MDL.
                tmpPagePropertiesObject = Object.assign(tmpPagePropertiesObject, this.pagePropertiesObject);
                if (this.isSqlmdl) {
                    tmpPagePropertiesObject.type = 2; // reassign it after object copy.
                    tmpPagePropertiesObject.servername = this._getServerConfigForFlatFileAndSQLmdl(this.serverip.value).serverName;
                    tmpPagePropertiesObject.port = this._getServerConfigForFlatFileAndSQLmdl(this.serverip.value).port;
                    tmpPagePropertiesObject.database = this.database.value;
                } else {
                    tmpPagePropertiesObject.type = 3; // reassign it after object copy.
                    tmpPagePropertiesObject.servername = this._getServerConfigForODBCmdl(this.serverip.value).serverName;
                    tmpPagePropertiesObject.port = this._getServerConfigForODBCmdl(this.serverip.value).port;
                    tmpPagePropertiesObject.servicename = this._getServerConfigForODBCmdl(this.serverip.value).serviceName;
                    tmpPagePropertiesObject.schema = this.database.value;
                }

                tmpPagePropertiesObject.username = this.loginUser.value;
                tmpPagePropertiesObject.password = this.sqlMdlPassword;
                tmpPagePropertiesObject.query = this.mdlQuery.value;

                if (this._operation === 'edit') {
                    this._dataProviderService.updateSQLItem(this._selectedRowId, tmpPagePropertiesObject).pipe(
                        take(1),
                        catchError((e) => {
                            this._toast.notify({
                                message: e.message,
                                style: NotificationStyles.Error
                            });
                            this.isProgressing = false;
                            return observableOf(null);
                        }),
                    ).subscribe((res) => {
                        if (res) {
                            let toastMessage: string = this.pagePropertiesObject.name;
                            if (this._dropedFile) {
                                toastMessage += ' has been imported into Tamale successfully.';
                            } else {
                                toastMessage += ' successfully updated.';
                            }
                            this.isProgressing = false;
                            this._toast.notify({
                                message: toastMessage,
                                style: NotificationStyles.Success
                            });
                            this._store.dispatch({
                                type: DataproviderActionTypes.INSERT_UPDATE_CONFIG,
                                payload: Dataprovider.parse(res),
                            });
                            this.selectedPreviewTypeList = [];
                            this.providerColumnList = [];
                            this._navigateBySelf = true;
                            this.isConnectSuccessMessageShow = false;
                            this._router.navigate(['settings', 'data']);
                        }
                    });
                } else {
                    this._dataProviderService.createSQLItem(tmpPagePropertiesObject).pipe(
                        take(1),
                        catchError((e) => {
                            this._toast.notify({
                                message: e.message,
                                style: NotificationStyles.Error
                            });
                            this.isProgressing = false;
                            return observableOf(null);
                        }),
                    ).subscribe((res) => {
                        if (res) {
                            this.isProgressing = false;
                            this._toast.notify({
                                message: this.pagePropertiesObject.name + ' has been imported into Tamale successfully.',
                                style: NotificationStyles.Success
                            });
                            this._store.dispatch({
                                type: DataproviderActionTypes.INSERT_UPDATE_CONFIG,
                                payload: Dataprovider.parse(res),
                            });
                            this.selectedPreviewTypeList = [];
                            this.providerColumnList = [];
                            this._navigateBySelf = true;
                            this.isConnectSuccessMessageShow = false;
                            this._router.navigate(['settings', 'data']);
                        }
                    });
                }
            }
        }
    }

    private _flatFileSuccessHandle(res) {
        let toastMessage = '';
        let style = -1;
        if (res['dataErrors']) {
            let errorColumnNumber = 0;
            // tslint:disable-next-line: forin
            for (const key in res['dataErrors']) {
                toastMessage += 'Column ' + res['dataErrors'][key]['name'] + ', ';
                errorColumnNumber++;
            }
            if (errorColumnNumber === 1) {
                toastMessage = toastMessage.substring(0, toastMessage.length - 2) + ' has invalid data, please have a check.';
            } else {
                toastMessage = toastMessage.substring(0, toastMessage.length - 2) + ' have invalid data, please have a check.';
            }
            style = NotificationStyles.Warning;
        } else {
            if (this._dropedFile) {
                toastMessage = this.pagePropertiesObject.name + ' has been imported into Tamale successfully.';
            } else {
                toastMessage = this.pagePropertiesObject.name + ' successfully updated.';
            }
            style = NotificationStyles.Success;
        }
        this._toast.notify({
            message: toastMessage,
            style: style
        });
        this._store.dispatch({
            type: DataproviderActionTypes.INSERT_UPDATE_CONFIG,
            payload: Dataprovider.parse(res),
        });
        this.selectedPreviewTypeList = [];
        this.providerColumnList = [];
        this._navigateBySelf = true;
        this._router.navigate(['settings', 'data']);
    }

    onBtnConnectClicked(): void {
        this.isConnectting = true;
        if (this._doSqlConnectValidation()) {
            if (this.isMDLConfigModified.password) {
                this.sqlMdlPassword = 'Basic ' + this._authService.encryptPasswordForWeb
                    (this.loginPassword.value, this.SQL_MDL_PASSWORD_KEY);
            } else {
                this.sqlMdlPassword = this.loginPassword.value;
            }
            let provider = null;
            if (this.isSqlmdl) {
                provider = {
                    'type': 2,
                    'name': this.name.value,
                    'servername': this._getServerConfigForFlatFileAndSQLmdl(this.serverip.value).serverName,
                    'port': this._getServerConfigForFlatFileAndSQLmdl(this.serverip.value).port,
                    'username': this.loginUser.value,
                    'password': this.sqlMdlPassword,
                };
            } else if (this.isOraclemdl) {
                provider = {
                    'type': 3,
                    'name': this.name.value,
                    'servername': this._getServerConfigForODBCmdl(this.serverip.value).serverName,
                    'port': this._getServerConfigForODBCmdl(this.serverip.value).port,
                    'servicename': this._getServerConfigForODBCmdl(this.serverip.value).serviceName,
                    'username': this.loginUser.value,
                    'password': this.sqlMdlPassword,
                };
            }
            if (provider) {
                this._dataProviderService.getSQLDatabases(provider).pipe(
                    catchError((error) => {
                        this._toast.notify({
                            message: error.message,
                            style: NotificationStyles.Error
                        });
                        return observableOf(null);
                    }),
                    take(1)
                ).subscribe(response => {
                    this.isConnectting = false;
                    if (response) {
                        this.isConnectedSuccess = true;
                        this.isConnectSuccessMessageShow = true;
                        this.databaseList = response;
                        if (this.isMDLConfigModified.serverip || this.isMDLConfigModified.userName || this.isMDLConfigModified.password) {
                            // to clean up the data when sql mdl config changed
                            this._handleUpdateSQLConfig();
                        } else {
                            this.isMDLConfigModified.serverip = false;
                            this.isMDLConfigModified.userName = false;
                            this.isMDLConfigModified.password = false;
                        }
                    } else {
                        this.isConnectedSuccess = false;
                        this.isConnectSuccessMessageShow = false;
                    }
                });
            }
        }
    }

    onBtnSQLQuery(): void {
        this.isRunning = true;
        this._doSqlRunValidation(); // this function need to run here to check database
        if (this._doSqlConnectValidation() && this._doSqlRunValidation()) {
            let provider = null;
            if (this.isSqlmdl) {
                // execute sql query
                provider = {
                    'type': 2,
                    'name': this.name.value,
                    'servername': this._getServerConfigForFlatFileAndSQLmdl(this.serverip.value).serverName,
                    'port': this._getServerConfigForFlatFileAndSQLmdl(this.serverip.value).port,
                    'username': this.loginUser.value,
                    'password': this.sqlMdlPassword,
                    'database': this.database.value,
                    'query': this.mdlQuery.value,
                };
            } else if (this.isOraclemdl) {
                provider = {
                    'type': 3,
                    'name': this.name.value,
                    'servername': this._getServerConfigForODBCmdl(this.serverip.value).serverName,
                    'port': this._getServerConfigForODBCmdl(this.serverip.value).port,
                    'servicename': this._getServerConfigForODBCmdl(this.serverip.value).serviceName,
                    'username': this.loginUser.value,
                    'password': this.sqlMdlPassword,
                    'schema': this.database.value,
                    'query': this.mdlQuery.value,
                };
            }

            if (provider) {
                // call api to get sql preview rows.
                this._dataProviderService.getSQLPreview(provider).pipe(
                    catchError((error) => {
                        this._toast.notify({
                            message: error.message,
                            style: NotificationStyles.Error
                        });
                        return observableOf(null);
                    }),
                    take(1)
                ).subscribe(response => {
                    this.isRunning = false;
                    if (response) {
                        this.isQuerySuccess = true;
                        this._setPreviewArea(response);
                    } else {
                        this.isQuerySuccess = false;
                    }
                });
            }
        }
    }

    handleDatabaseChange(item: any): void {
        this.isQuerySuccess = false;
    }

    /**
     * Change preview options dropdownlist, needs to map pagePropertiesObject.columns and columnTypeOptions
     * due to server resonse data format limit.
     *
     * @memberof DataImportComponent
     */
    handlePreviewOptionChange(item: any) {
        /**
         * this.pagePropertiesObject.columns Array data format:
                0: {name: "name", type: "character varying(255)"}
                1: {name: "age", type: "date", format: "MDY"}
                2: {name: "message", type: "numeric"}
                3: {name: "adhoc", type: "date", format: "DMY"}
                4: {name: "adhoc1", type: "date", format: "YMD"}

            columnTypeOptions = [
                { name: 'Text', value: 'character varying(255)' },
                { name: 'Number', value: 'numeric' },
                { name: 'Date (DD/MM/YYYY)', value: 'date', format: 'DMY' },
                { name: 'Date (MM/DD/YYYY)', value: 'date', format: 'MDY' },
                { name: 'Date (YYYY/MM/DD)', value: 'date', format: 'YMD' }
            ];

            please read the 2 data structures above, pagePropertiesObject.columns is server response data,
            columnTypeOptions is the contract with server. please note 2 things here:
            1. In tamale web 1.0, there isn't format element. the format is as below.
                columnTypeOptions = [
                    { name: 'Text', value: 'character varying(255)' },
                    { name: 'Number', value: 'numeric' },
                    { name: 'Date', value: 'date'  }
                ];
                In Tamale web 2.0. it needs to support international date, date turns to DMY/MDY/YMD.
            2. for selected option item, it's [(ngModel)]="selectedPreviewTypeList[i]", since there are
            multiple columns, so we use array and set selectedPreviewTypeList.length === columns.count.
        */
        this.selectedPreviewTypeList.forEach((columnTypeOption, index) => {
            // make the date column have that depend on each date column change
            // which will result other date column changes as well as the same date format setting per BA request.
            if (item && item.value === 'date') {
                this.selectedPreviewTypeList.forEach((type, i) => {
                    if (type.value === 'date') {
                        this.selectedPreviewTypeList[i] = item;
                    }
                });
            }

            this.pagePropertiesObject.columns[index]['type'] = columnTypeOption.value;
            if (columnTypeOption.value === 'date') {
                if (this.pagePropertiesObject.columns[index].hasOwnProperty('format')) {
                    this.pagePropertiesObject.columns[index]['format'] = columnTypeOption.format;
                } else {
                    this.pagePropertiesObject.columns[index] = {
                        name: this.pagePropertiesObject.columns[index].name,
                        type: columnTypeOption.value,
                        format: columnTypeOption.format
                    };
                }
            } else {
                if (this.pagePropertiesObject.columns[index].hasOwnProperty('format')) {
                    delete this.pagePropertiesObject.columns[index].format;
                }
            }
        });

        // keep it a copy.
        this.originalPagePropertiesObject.columns = _lodash.cloneDeep(this.pagePropertiesObject.columns);

        // providerColumnList depands on the type of every column, only text / number column is available in it.
        this.providerColumnList = this.pagePropertiesObject.columns
            .filter(col => col.type === ProviderColumnType.STRING || col.type === ProviderColumnType.NUMBER)
            .map(col => col.name);

        // set providerColumnList dropdown control title to empty if it's not text anymore.
        if (!this.providerColumnList.includes(this.providerColumn.value)) {
            this.providerColumn.setValue('');
        }
    }

    /**
     *  filter for the dropdown list of provider column.
     *
     * @param {*} filter
     * @memberof DataImportComponent
     */
    handleProviderColumnFilter(filter: any): void {
        this.pagePropertiesObject.providerColumn = filter;
        this.originalPagePropertiesObject.providerColumn = filter;

        if (filter === '') {
            if (this.pagePropertiesObject.filename) {
                this.providerColumnList = this.pagePropertiesObject.columns
                    .filter(col => col.type === ProviderColumnType.STRING || col.type === ProviderColumnType.NUMBER)
                    .map(col => col.name);
            } else {
                this.providerColumn.setValue('');
            }
        } else {
            this.providerColumnList = this.providerColumnList.filter((s) => s.toLowerCase().indexOf(filter.toLowerCase()) !== -1);
        }
    }

    /**
     * Entity Referrence changes
     *
     * @param {*} value
     * @memberof DataImportComponent
     */
    handleSelectionChange(value: any): void {
        this.pagePropertiesObject.entityReference = value;
        this.originalPagePropertiesObject.entityReference = value;

        // There are only 3 values: ['Shortname', 'Name', 'Alias'];
        if (value.toLowerCase() === 'alias') {
            this.aliasPrefix.setValue(this.pagePropertiesObject.aliasPrefix);
        } else {
            this.aliasPrefix.setValue('');
        }
    }

    isDirty(): boolean {
        return this._navigateBySelf ? false : (this.flatfileForm.dirty || this.isFileChanged);
    }

    trackByFn(index) {
        return index;
    }

    private _initUpload(filename: string) {
        this.uploadEditing = 0; // represents number of elements in upload widget being editing
        this.uploadConfig = {
            files: [],
            threadFileNameCount: new Map<string, number>(),
            url: AppConfig.dataproviderEndPoint, // /restapi/2.1/dataprovider/.
            acceptedFiles: '.csv',
            maxFiles: 1,
            maxFilesize: 250,
            paramName: 'attachment', // required by restAPI
            parallelUploads: 1,
            autoQueue: true, // Make sure the files aren't queued until manually added
            autoProcessQueue: false, // Not to upload file at once on droping file.
            dictMaxFilesExceeded: 'You can not upload more than 1 file.', // overwrite dict..Option in case that dropzone.js changes it.
            dictFileTooBig: 'You can not upload file larger than 250MB.',
            onError: (errorMessage) => {
            },
            readOnly: false,
            dropzone: null,
            upload: null,
            onAddedfile: (file, xhr, config: TamUploadConfig) => {
                // check file extension, only CSV is allowed
                const reg = /\.csv$/g;
                if (!reg.test(file.name)) {
                    this._toast.notify({
                        message: 'Only CSV can be uploaded.',
                        style: NotificationStyles.Error
                    });
                    return;
                }

                this.isFileChanged = true;

                // clear Data Preview cache and keep history data
                this.originalSelectedPreviewTypeList = _lodash.cloneDeep(this.selectedPreviewTypeList);
                this.originalPagePropertiesObject = _lodash.cloneDeep(this.pagePropertiesObject);
                this.selectedPreviewTypeList = [];
                this.providerColumnList = [];
                this.entityReferenceOptions.setValue('Shortname');

                // enable save button
                this.btnDisabled = false;

                // check file if new added file or existing file
                let acceptedFile = new TamUploadUIFile();
                if (file.upload !== undefined && file.upload.uuid) {
                    acceptedFile.name = file.name;

                    // set progress as 100% and serverName to mark it as finished upload.
                    acceptedFile.progress = '100%';
                    acceptedFile.isCompleted = true;
                    acceptedFile.serverName = file.name;
                } else {
                    // on update file case
                    acceptedFile = file;
                }
                this._dropedFile = file;
                config.files = [];
                // hide UI valid message.
                this.isFileValid = true;
                // update dropzone UI to show file on it.
                config.files.push(acceptedFile);
                // below will detect encoding
                const fileReader = new FileReader();
                fileReader.onload = (e: any) => {
                    const encoding = jschardet.detect(e.target.result, { minimumThreshold: 0 });
                    this._handleAddedFile(file, encoding.encoding);
                };
                fileReader.readAsBinaryString(file);
            },
            onSending: (file, xhr, config) => {
            },
            onSuccess: (file, responseObject, config) => {
            },
            onRemove: (file, dropzone, inputConfig) => {
                // disable save button
                this.btnDisabled = true;
                this.isFileChanged = true; // used on cancel clicked.
                dropzone.removeAllFiles(true); // clear dropzone
                inputConfig.files = []; // clear preview zone.
                this._handleRemoveFile(file);
                return false; // Mark the file uploaded, go to 'else' in tam-upload remove.
            },
            onProgress: (file, progress, config) => {
            },
            onStatusChange: (workflow, changedEditing) => {
                this.uploadEditing += changedEditing;
            },
            onNameChange: (_filename, oldfilename, config, index) => {
            },
            isDownloadable: false,
            isPreviewable: false
        };

        // show existing file on dropzone as 100% uploaded status.
        if (filename && filename.trim().length > 0) {
            this.uploadConfig.files.push(this._parseTamUploadUIFile(filename));
        }
        // set save button disabled if no change.
        this.btnDisabled = true;
    }

    serverConfigChanged() {
        this.isConnectSuccessMessageShow = false;
        this.isMDLConfigModified.serverip = true;
        this.isMDLConfigModified.userName = true;
        this.isConnectedSuccess = false;
    }

    serverPasswordChanged() {
        this.isConnectSuccessMessageShow = false;
        this.isMDLConfigModified.password = true;
        this.isConnectedSuccess = false;
    }

    sqlQueryChanged() {
        this.isQuerySuccess = false;
    }

    private _getServerConfigForFlatFileAndSQLmdl(serverip) {
        let serverName = '';
        let port = '1433';
        if (serverip && serverip.split(':').length > 1) {
            if (serverip.split(':')[1]) {
                port = serverip.split(':')[1];
            }
            serverName = serverip.split(':')[0];
        } else {
            serverName = serverip;
        }
        return {
            port: port,
            serverName: serverName
        };
    }

    private _getServerConfigForODBCmdl(serverip) {
        let serverName = '';
        let port = '1521';
        let serviceName = '';
        if (serverip && serverip.split(':').length > 1 && serverip.split(':')[1]) {
            // server name, port, service name exist
            serverName = serverip.split(':')[0];
            const portAndServiceName = serverip.split(':')[1];
            if (portAndServiceName.split('/').length > 1) {
                port = portAndServiceName.split('/')[0];
                serviceName = portAndServiceName.split('/')[1];
            } else {
                port = serverip.split(':')[1];
            }
        } else if (serverip && serverip.split('/').length > 1) {
            // only have the server name and service name
            serverName = serverip.split('/')[0];
            serviceName = serverip.split('/')[1];
        } else {
            // only have the server name
            serverName = serverip;
        }
        return {
            serverName: serverName,
            port: port,
            serviceName: serviceName
        };
    }

    private _setPreviewArea(results) {
        this.pagePropertiesObject.filename = this.name.value;

        this.pagePropertiesObject.columns = results.columns;

        this.pagePropertiesObject.previewRows = results.previewRows;

        // bind it to UI.
        this.providerColumnList = this.pagePropertiesObject.columns
            .filter(col => col.type === ProviderColumnType.STRING || col.type === ProviderColumnType.NUMBER)
            .map(col => col.name);

        // set providerColumn to empty on new file imported
        this.providerColumn.setValue('');
    }

    private _doValidation(): boolean {
        // dataValid: this var is to define if we are able to send request to server when all the fields are ok
        let dataValid = true;
        this.isFileValid = true;
        this.name.setErrors(null);
        this.description.setErrors(null);

        if (!this.name.value) {
            this.name.setErrors({
                required: true
            });
            dataValid = false;
        } else if (this.name.value.length > 64) {
            this.name.setErrors({
                maxlength: true
            });
            dataValid = false;
        }

        if (this.description && this.description.value) {
            if (this.description.value.length > 128) {
                this.description.setErrors({
                    maxlength: true
                });
                dataValid = false;
            }
        }

        if (!this.providerColumn.value || this.providerColumn.value === '') {
            this.providerColumn.setErrors({
                required: true
            });
            dataValid = false;
        }

        if (!this.pagePropertiesObject.filename) {
            this.isFileValid = false;
        }
        // the below condition cannot write together, or the second function will not work
        if (this.isSqlmdl || this.isOraclemdl) {
            if (!this._doSqlConnectValidation()) {
                dataValid = false;
            }
            if (!this._doSqlRunValidation()) {
                dataValid = false;
            }
        }
        return dataValid;
    }

    private _doSqlConnectValidation(): boolean {
        let connectSuccess = true;
        if (!this.serverip.value) {
            this.serverip.setErrors({
                required: true
            });
            connectSuccess = false;
        }

        if (!this.loginUser.value) {
            this.loginUser.setErrors({
                required: true
            });
            connectSuccess = false;
        }

        if (!this.loginPassword.value) {
            this.loginPassword.setErrors({
                required: true
            });
            connectSuccess = false;
        }
        return connectSuccess;
    }

    private _doSqlRunValidation(): boolean {
        let querySuccess = true;
        if (!this.database.value) {
            this.database.setErrors({
                required: true
            });
            querySuccess = false;
        }

        if (!this.mdlQuery.value) {
            this.mdlQuery.setErrors({
                required: true
            });
            querySuccess = false;
        }
        return querySuccess;
    }

    private _handleRemoveFile(file: any) {
        this.pagePropertiesObject.previewRows = false;
        this.pagePropertiesObject.filename = '';
        this.selectedPreviewTypeList = [];
        this.providerColumnList = [];
        this.originalSelectedPreviewTypeList = [];
        this.providerColumn.setValue('');
        this.pagePropertiesObject.entityReference = 'SHORTNAME';
        this.entityReferenceOptions.setValue('ShortName');
    }

    private _handleUpdateSQLConfig() {
        this.database.setValue('');
        this.mdlQuery.setValue('');
        this.providerColumnList = [];
        this.pagePropertiesObject.previewRows = false;
        this.providerColumn.setValue('');
        this.pagePropertiesObject.entityReference = 'SHORTNAME';
        this.entityReferenceOptions.setValue('ShortName');
    }

    private _parseTamUploadUIFile(filename): TamUploadUIFile {
        const tamUploadUIFile = new TamUploadUIFile();
        tamUploadUIFile.name = filename;
        tamUploadUIFile.isNew = false;
        tamUploadUIFile.isCompleted = true;
        // set progress as 100% and serverName to mark it as finished upload.
        tamUploadUIFile.progress = '100%';
        tamUploadUIFile.serverName = filename;
        return tamUploadUIFile;
    }

    /**
     * parse file to preview area.
     *
     * @private
     * @param {*} file
     * @memberof DataImportComponent
     */
    private _handleAddedFile(file: any, encoding: string) {
        this._parseCSVData(this.pagePropertiesObject, this.originalPagePropertiesObject,
            this.providerColumnList,
            this.selectedPreviewTypeList, this.originalSelectedPreviewTypeList,
            this.entityReferenceOptions,
            this.providerColumn,
            this.columnTypeOptions, file, this.callBackToBindUI, encoding);
    }

    /**
     * get flatfile data by id on Edit.
     *
     * @private
     * @param {*} id
     * @memberof DataImportComponent
     */
    private _loadFlatfileData(id): void {
        this._dataProviderService.queryById(id).pipe(
            take(1),
            catchError((e) => {
                return observableOf();
            })).subscribe((res) => {
                this.pagePropertiesObject = res ? res.provider : null;

                if (this.pagePropertiesObject && this.pagePropertiesObject.filename && this.pagePropertiesObject.columns) {

                    this.uploadConfig.files.push(this._parseTamUploadUIFile(this.pagePropertiesObject.filename));

                    this.pageTitle = this.pagePropertiesObject.name;
                    this.name.setValue(this.pagePropertiesObject.name);
                    this.description.setValue(this.pagePropertiesObject.description);
                    this.providerColumn.setValue(this.pagePropertiesObject.providerColumn);

                    // entityReferenceNameList = ['Shortname', 'Name', 'Alias'];
                    this.entityReferenceOptions.setValue(this._parseEntityReference(this.pagePropertiesObject.entityReference.toUpperCase()));

                    this.aliasPrefix.setValue(this.pagePropertiesObject.aliasPrefix);

                    /** There are only 3 types, but for date type, there are 3 more format: YMD,MDY,DMY.
                     * this.pagePropertiesObject.columns Array data format:
                            0: {name: "name", type: "character varying(255)"}
                            1: {name: "age", type: "date", format: "MDY"}
                            2: {name: "message", type: "numeric"}
                            3: {name: "adhoc", type: "date", format: "DMY"}
                            4: {name: "adhoc1", type: "date", format: "YMD"}

                        columnTypeOptions = [
                            { name: 'Text', value: 'character varying(255)' },
                            { name: 'Number', value: 'numeric' },
                            { name: 'Date (DD/MM/YYYY)', value: 'date', format: 'DMY' },
                            { name: 'Date (MM/DD/YYYY)', value: 'date', format: 'MDY' },
                            { name: 'Date (YYYY/MM/DD)', value: 'date', format: 'YMD' }
                        ];
                    */
                    this.pagePropertiesObject.columns.forEach((column) => {
                        // tslint:disable-next-line: no-shadowed-variable
                        for (const { option, index } of this.columnTypeOptions.map((option, index) => ({ option, index }))) {
                            if (column.type === 'date') {
                                // if data is from Tamale Web 1.0, it doesn't have 'format' property. use default MDY.
                                if (!column.hasOwnProperty('format')) {
                                    // uses { name: 'Date (MM/DD/YYYY)', value: 'date', format: 'MDY' } by default.
                                    this.selectedPreviewTypeList.push(this.columnTypeOptions[3]);
                                    break;
                                } else if (column.format && (column.format === option.format)) {
                                    this.selectedPreviewTypeList.push(this.columnTypeOptions[index]);
                                    break;
                                }
                            } else if (column.type === option.value) {
                                this.selectedPreviewTypeList.push(this.columnTypeOptions[index]);
                                break;
                            }
                        }
                    });

                    // set provider column with only Text/Number type.
                    this.providerColumnList = this.pagePropertiesObject.columns
                        .filter(col => col.type === ProviderColumnType.STRING || col.type === ProviderColumnType.NUMBER)
                        .map(col => col.name);

                    // keep a copy for user 'select files' case.
                    this.originalSelectedPreviewTypeList = _lodash.cloneDeep(this.selectedPreviewTypeList);
                    this.originalPagePropertiesObject = _lodash.cloneDeep(this.pagePropertiesObject);
                }
            });
    }

    /**
     * load ODBC MDL Data by id
     *
     * @private
     * @param {*} id
     * @memberof DataImportComponent
     */
    private _loadODBCmdlData(id): void {
        this._dataProviderService.queryById(id).pipe(
            take(1),
            catchError((e) => {
                return observableOf();
            })).subscribe((res) => {
                let tmpPagePropertiesObject = {
                    description: '', entityReference: 'SHORTNAME', aliasPrefix: '',
                    type: 3, name: '', filename: '', columns: [], providerColumn: '',
                    previewRows: false,
                    servername: '',
                    port: '',
                    username: '',
                    password: '',
                    schema: '',
                    query: '',
                    servicename: '',
                };

                tmpPagePropertiesObject = res ? res.provider : null;
                this.pagePropertiesObject = tmpPagePropertiesObject;
                if (tmpPagePropertiesObject && tmpPagePropertiesObject.columns) {

                    this.pageTitle = tmpPagePropertiesObject.name;
                    this.name.setValue(tmpPagePropertiesObject.name);
                    this.description.setValue(tmpPagePropertiesObject.description);
                    this.providerColumn.setValue(tmpPagePropertiesObject.providerColumn);

                    this.entityReferenceOptions.setValue(this._parseEntityReference(this.pagePropertiesObject.entityReference.toUpperCase()));
                    this.aliasPrefix.setValue(tmpPagePropertiesObject.aliasPrefix);

                    // set provider column with only Text type.
                    this.providerColumnList = tmpPagePropertiesObject.columns
                        .filter(col => col.type === ProviderColumnType.STRING || col.type === ProviderColumnType.NUMBER)
                        .map(col => col.name);

                    tmpPagePropertiesObject.servername = tmpPagePropertiesObject.servername + ':' + tmpPagePropertiesObject.port
                        + '/' + tmpPagePropertiesObject.servicename;
                    this.serverip.setValue(tmpPagePropertiesObject.servername);
                    this.loginUser.setValue(tmpPagePropertiesObject.username);
                    this.loginPassword.setValue(tmpPagePropertiesObject.password);
                    this.database.setValue(tmpPagePropertiesObject.schema);
                    this.mdlQuery.setValue(tmpPagePropertiesObject.query);
                    this.onBtnConnectClicked();
                }
            });
    }

    /**
     * load SQL MDL Data by id
     *
     * @private
     * @param {*} id
     * @memberof DataImportComponent
     */
    private _loadSqlmldData(id): void {
        this._dataProviderService.queryById(id).pipe(
            take(1),
            catchError((e) => {
                return observableOf();
            })).subscribe((res) => {
                let tmpPagePropertiesObject = {
                    description: '', entityReference: 'SHORTNAME', aliasPrefix: '',
                    type: 2, name: '', filename: '', columns: [], providerColumn: '',
                    previewRows: false,
                    servername: '',
                    port: '',
                    username: '',
                    password: '',
                    database: '',
                    query: ''
                };

                tmpPagePropertiesObject = res ? res.provider : null;
                this.pagePropertiesObject = tmpPagePropertiesObject;
                if (tmpPagePropertiesObject && tmpPagePropertiesObject.columns) {

                    this.pageTitle = tmpPagePropertiesObject.name;
                    this.name.setValue(tmpPagePropertiesObject.name);
                    this.description.setValue(tmpPagePropertiesObject.description);
                    this.providerColumn.setValue(tmpPagePropertiesObject.providerColumn);

                    this.entityReferenceOptions.setValue(this._parseEntityReference(this.pagePropertiesObject.entityReference.toUpperCase()));
                    this.aliasPrefix.setValue(tmpPagePropertiesObject.aliasPrefix);

                    // set provider column with only Text type.
                    this.providerColumnList = tmpPagePropertiesObject.columns
                        .filter(col => col.type === ProviderColumnType.STRING || col.type === ProviderColumnType.NUMBER)
                        .map(col => col.name);
                    if (tmpPagePropertiesObject.port !== '1433') {
                        tmpPagePropertiesObject.servername = tmpPagePropertiesObject.servername + ':' + tmpPagePropertiesObject.port;
                    }
                    this.serverip.setValue(tmpPagePropertiesObject.servername);
                    this.loginUser.setValue(tmpPagePropertiesObject.username);
                    this.loginPassword.setValue(tmpPagePropertiesObject.password);
                    this.database.setValue(tmpPagePropertiesObject.database);
                    this.mdlQuery.setValue(tmpPagePropertiesObject.query);
                    this.onBtnConnectClicked();
                }
            });
    }

    /**
    * Parses CSV to data. It's hosted in browser using Papaparse lib.
    * https://www.papaparse.com/docs
    * The return data is in 'complete' callback function, it only can be used
    * in its thread, so we have to use callBackToSendRequest function to access
    * result data and pass service to consume it.
     *
     * @private
     * @param {*} pagePropertiesObject current UI binding data, representing the values to the data-import page UI elemments.
     * @param {*} originalPagePropertiesObject keep the last pagePropertiesObject for changing file.
     * @param {*} providerColumnList providerColumn dropdown List
     * @param {*} selectedPreviewTypeList List of the Data Preview column types
     * @param {*} originalSelectedPreviewTypeListkeep the the last selectedPreviewTypeList for changing file.
     * @param {*} entityReferenceOptions passing its reference/pointer into Papaparse.parse callback
     * @param {*} providerColumn  passing its reference/pointer into Papaparse.parse callback
     * @param {*} columnTypeOptions passing its reference/pointer into Papaparse.parse callback
     * @param {*} localFile the CSV file
     * @param {*} callBackToBindUI Papaparse.parse callback function
     * @memberof DataImportComponent
     */
    private _parseCSVData(
        pagePropertiesObject,
        originalPagePropertiesObject,
        providerColumnList,
        selectedPreviewTypeList,
        originalSelectedPreviewTypeList,
        entityReferenceOptions,
        providerColumn,
        columnTypeOptions,
        localFile,
        callBackToBindUI,
        encoding: string,
    ) {
        Papaparse.parse(localFile, {
            complete: function (results, file) {
                callBackToBindUI(
                    results,
                    file,
                    pagePropertiesObject,
                    originalPagePropertiesObject,
                    providerColumnList,
                    selectedPreviewTypeList,
                    originalSelectedPreviewTypeList,
                    entityReferenceOptions,
                    providerColumn,
                    columnTypeOptions);
            },
            encoding: encoding,
        });
    }

    private callBackToBindUI(
        results,
        file,
        pagePropertiesObject,
        originalPagePropertiesObject,
        providerColumnList,
        selectedPreviewTypeList,
        originalSelectedPreviewTypeList,
        entityReferenceOptions,
        providerColumn,
        columnTypeOptions
    ) {
        // for edit file case
        const resultsData = _lodash.cloneDeep(results.data[0]); // the new file header

        // compare two array ignoring order.
        const newFileHeader = JSON.stringify(resultsData.sort());
        const lastFileHeader = JSON.stringify(originalPagePropertiesObject.columns.map(x => x.name).sort());

        if (newFileHeader === lastFileHeader) {
            const tmpPagePropertiesObject = {
                description: '',
                type: 1, name: '', filename: '', columns: [], previewRows: false
            };
            tmpPagePropertiesObject.filename = file.name; // source: the cvs file name

            // set current EntityReferenceValue.
            let currentEntityReferenceValue = 'Shortname';
            const value = originalPagePropertiesObject.entityReference.toUpperCase();
            if (value.toUpperCase() === 'SHORTNAME') {
                currentEntityReferenceValue = 'Shortname';
            } else if (value.toUpperCase() === 'NAME') {
                currentEntityReferenceValue = 'Name';
            } else {
                currentEntityReferenceValue = 'Alias';
            }
            entityReferenceOptions.setValue(currentEntityReferenceValue);

            // map new file columns.
            // eg: According col name 'adhoc1' to set it as {name: "adhoc1", type: "date", format: "YMD"}
            tmpPagePropertiesObject.columns = results.data[0].map(col => originalPagePropertiesObject.columns.find(obj => {
                return obj.name === col;
            }));

            // find correct columnTypeOptions item via updated pagePropertiesObject.columns.
            tmpPagePropertiesObject.columns.forEach((item) => {
                const i = columnTypeOptions.findIndex(obj => {
                    if (item.type !== 'date' && item.type === obj.value) {
                        return true;
                    } else if (item.type === 'date' && item.format === obj.format) {
                        return true;
                    } else {
                        return false;
                    }
                });

                // it has to use columnTypeOptions item or else won't bind to UI.
                selectedPreviewTypeList.push(columnTypeOptions[i]);
            });

            tmpPagePropertiesObject.previewRows = results.data.filter(function (currentValue, index) {
                return index > 0 && index < 6;
            });

            // bind it to UI.
            providerColumnList = Object.assign(providerColumnList, tmpPagePropertiesObject.columns
                .filter(col => col.type === ProviderColumnType.STRING || col.type === ProviderColumnType.NUMBER)
                .map(col => col.name));

            pagePropertiesObject = Object.assign(pagePropertiesObject, tmpPagePropertiesObject);

        } else {

            const tmpPagePropertiesObject = {
                description: '', entityReference: 'SHORTNAME', aliasPrefix: '',
                type: 1, name: '', filename: '', columns: [], previewRows: false
            };

            tmpPagePropertiesObject.filename = file.name; // source: the cvs file name
            tmpPagePropertiesObject.columns = results.data[0].map(function (col) {
                return { name: col, type: 'character varying(255)' };
            });

            tmpPagePropertiesObject.previewRows = results.data.filter(function (currentValue, index) {
                return index > 0 && index < 6;
            });

            // bind it to UI.
            providerColumnList = Object.assign(providerColumnList, tmpPagePropertiesObject.columns.map(col => col.name));
            pagePropertiesObject = Object.assign(pagePropertiesObject, tmpPagePropertiesObject);

            tmpPagePropertiesObject.columns.forEach((column) => {
                selectedPreviewTypeList.push(columnTypeOptions[0]);
            });

            // set providerColumn to empty on new file imported
            providerColumn.setValue('');
        }

        originalSelectedPreviewTypeList = _lodash.cloneDeep(selectedPreviewTypeList);
    }

    private _parseEntityReference(value: string): string {
        if (value.toUpperCase() === 'SHORTNAME') {
            return 'Shortname';
        } else if (value.toUpperCase() === 'NAME') {
            return 'Name';
        } else {
            return 'Alias';
        }
    }
}
